import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@main/complykit';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import {
  GetSharepointFileMetadataQueryVariables,
  useLazyGetSharepointFileMetadataQuery,
} from '../integration-handler-config/config-sharepoint/config-sharepoint.generated';

export type ResolvedSharepointFile = {
  driveId: string;
  driveName: string;
  fileId: string;
  fileName: string;
  filePath: string;
  webUrl: string;
};

export type SharepointFileUrlInputRef = {
  submit: () => Promise<ResolvedSharepointFile | null>;
  reset: () => void;
};

export type SharepointFileUrlInputProps = {
  orgIntegrationId: string;
  isDisabled?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  label?: string;
};

const formSchema = z.object({
  shareLink: z.url({ message: 'configureForm.validation.url' }),
});

type FormSchema = z.infer<typeof formSchema>;

const defaultValues: FormSchema = {
  shareLink: '',
};

export const SharepointFileUrlInput = forwardRef<
  SharepointFileUrlInputRef,
  SharepointFileUrlInputProps
>(({ orgIntegrationId, isDisabled, onDirtyChange, label }, ref) => {
  const { t } = useTranslation('integrations');

  const {
    control,
    setError,
    formState: { isDirty },
    getValues,
    trigger,
    reset: formReset,
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const getSharepointFileMetadata = useGetSharepointFileMetadata();

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const isValid = await trigger();
      if (!isValid) return null;
      const { shareLink } = getValues();
      const metadataResult = await getSharepointFileMetadata({
        orgIntegrationId,
        fileUrl: shareLink,
      });
      if (!metadataResult) return null;
      if ('errorMsg' in metadataResult) {
        setError('shareLink', { message: metadataResult.errorMsg });
        return null;
      }
      formReset(defaultValues);
      return { ...metadataResult, webUrl: shareLink };
    },
    reset: () => {
      formReset(defaultValues);
    },
  }));

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Controller
      control={control}
      name="shareLink"
      render={({ field, fieldState }) => (
        <TextField
          ref={field.ref}
          type="url"
          label={label ?? t('integrations.sharepoint.evidenceConfig.newLinkInput.label')}
          name={field.name}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isDisabled={isDisabled}
          error={fieldState.error?.message && t(fieldState.error.message as never)}
        />
      )}
    />
  );
});
SharepointFileUrlInput.displayName = 'SharepointFileUrlInput';

function useGetSharepointFileMetadata() {
  const { t } = useTranslation('integrations');
  const [getSharepointFileMetadata] = useLazyGetSharepointFileMetadataQuery();

  return async (input: GetSharepointFileMetadataQueryVariables) => {
    const { sharepoint_file_metadata: metadata } = await getSharepointFileMetadata(input).unwrap();
    if (metadata?.error) {
      const errorMsg = t(
        `integrations.sharepoint.evidenceConfig.newLinkInput.errorMsg.${metadata.error.type}`,
      );
      return { errorMsg };
    }
    if (metadata?.success) {
      return metadata.success;
    }
    return null;
  };
}
